import React, { forwardRef, useEffect, useState, useImperativeHandle } from 'react';
import { Form, Input, DatePicker, Button, Checkbox, Divider } from 'antd';
import { ProviderExperience } from 'types/provider';
import { IBackgroundInfo } from 'types/newUniprofile';
import ExperiencePlaceholder from 'assets/common/experiencePlaceholder.svg';
import dayjs from 'dayjs';
import s from './s.module.less';

interface MultiProviderExperience {
    providerExperienceList: ProviderExperience[];
}
interface Props {
    initValue?: ProviderExperience[];
    isEditing?: boolean;
    updateFn: (data: Partial<IBackgroundInfo>) => Promise<void>;
}

const HealthcareExperience = forwardRef((props: Props, ref) => {
    const { isEditing, initValue, updateFn } = props;
    const [form] = Form.useForm<MultiProviderExperience>();
    const [update, forceUpdate] = useState({});

    useEffect(() => {
        if (initValue?.length) {
            form.setFieldsValue({
                providerExperienceList: initValue.map((item) => ({
                    ...item,
                    startDate: item.startDate ? dayjs(item.startDate) : undefined,
                    endDate: item.endDate ? dayjs(item.endDate) : undefined,
                })),
            });
        }
    }, [initValue]);

    const onSubmit = async () => {
        try {
            const values = await form.validateFields();
            await updateFn({
                providerExperienceList: values.providerExperienceList,
            });
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    useImperativeHandle(ref, () => ({
        onSubmit,
    }));

    const renderAdd = (add: () => void) => {
        return (
            <div
                onClick={() => {
                    add();
                    const wrap = document.getElementById('practiceLicense');
                    setTimeout(() => {
                        wrap?.parentElement?.parentElement?.scroll({
                            top: 1000000,
                            behavior: 'smooth',
                        });
                    }, 200);
                }}
                className={s.add}
            >
                + Add another practice license
            </div>);
    };

    const renderEditing = () => {
        return (
            <Form
                form={form}
                autoComplete="off"
                layout="vertical"
                scrollToFirstError
            >
                <Form.List name="providerExperienceList" initialValue={[{}]}>
                    {
                        (fields, { add, remove }) => {
                            return (
                                <>
                                    {
                                        fields.map(({ key, name, ...restField }, index) => {
                                            const currentWorkingOn = form.getFieldValue(['providerExperienceList', name, 'currently']);

                                            return (
                                                <div>
                                                    <div key={key} className={s.wrap}>
                                                        <div
                                                            id={`Experience_${index + 1}`}
                                                        />
                                                        <div className={s.label}>{`Experience ${index + 1}`}</div>
                                                        <Divider style={{ margin: '4px 0px 12px' }} />
                                                        <div className={s.additionalForm}>
                                                            <Form.Item
                                                                {...restField}
                                                                label="Organization name"
                                                                name={[name, 'organizationName']}
                                                                rules={[
                                                                    { required: true, message: 'Organization name is a required field' },
                                                                ]}
                                                            >
                                                                <Input type="text" placeholder="Ex. Hospital or clinic or practice name" />
                                                            </Form.Item>
                                                            <Form.Item
                                                                {...restField}
                                                                label="Title"
                                                                name={[name, 'title']}
                                                                style={{ marginBottom: '8px' }}
                                                                validateFirst
                                                                rules={[
                                                                    { required: true, message: 'Title is a required field' },
                                                                ]}
                                                            >
                                                                <Input type="text" placeholder="Ex. Nurse practitioner" />
                                                            </Form.Item>
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'currently']}
                                                                valuePropName="checked"
                                                            >
                                                                <Checkbox
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            form.setFieldValue(['providerExperienceList', name, 'endDate'], undefined);
                                                                        }
                                                                        forceUpdate({});
                                                                    }}
                                                                >
                                                                    I am currently working in this role
                                                                </Checkbox>
                                                            </Form.Item>

                                                            <div className="flex gap-4">
                                                                <Form.Item
                                                                    {...restField}
                                                                    style={{ flex: 1 }}
                                                                    label="Start Date"
                                                                    name={[name, 'startDate']}
                                                                    rules={[
                                                                        {
                                                                            validator: (rules, value) => {
                                                                                const endDateValue = form.getFieldValue('providerExperienceList')?.[index]?.endDate;

                                                                                if (endDateValue && endDateValue.isBefore(value)) {
                                                                                    return Promise.reject(
                                                                                        new Error('start date cannot be later than the end date'),
                                                                                    );
                                                                                }
                                                                                return Promise.resolve(true);
                                                                            },
                                                                        },
                                                                    ]}
                                                                >
                                                                    <DatePicker
                                                                        onChange={() => {
                                                                            forceUpdate({});
                                                                            form.validateFields();
                                                                        }}
                                                                        allowClear
                                                                        className={s.datepicker}
                                                                        picker="month"
                                                                        disabledDate={(current) => {
                                                                            const endDateValue = form.getFieldValue('providerExperienceList')?.[index]?.endDate;
                                                                            if (!endDateValue || currentWorkingOn) {
                                                                                return false;
                                                                            }
                                                                            return current.valueOf() > endDateValue.valueOf();
                                                                        }}
                                                                        style={{ width: '100%' }}
                                                                    />
                                                                </Form.Item>

                                                                {!currentWorkingOn &&
                                                                    <Form.Item
                                                                        {...restField}
                                                                        style={{ flex: 1 }}
                                                                        label="End Date"
                                                                        name={[name, 'endDate']}
                                                                        rules={[
                                                                            {
                                                                                validator: (rules, value) => {
                                                                                    const startDateValue = form.getFieldValue('providerExperienceList')?.[index]?.startDate;

                                                                                    if (startDateValue && value && value.isBefore(startDateValue)) {
                                                                                        return Promise.reject(
                                                                                            new Error('start date cannot be later than the end date'),
                                                                                        );
                                                                                    }

                                                                                    return Promise.resolve(true);
                                                                                },
                                                                            },
                                                                        ]}
                                                                    >
                                                                        <DatePicker
                                                                            onChange={() => {
                                                                                forceUpdate({});
                                                                                form.validateFields();
                                                                            }}
                                                                            allowClear
                                                                            className={s.datepicker}
                                                                            picker="month"
                                                                            disabledDate={(current) => {
                                                                                const startDateValue = form.getFieldValue('providerExperienceList')?.[index]?.startDate;
                                                                                if (!startDateValue) {
                                                                                    return false;
                                                                                }
                                                                                return current.valueOf() < startDateValue.valueOf();
                                                                            }}
                                                                            style={{ width: '100%' }}
                                                                        />
                                                                    </Form.Item>

                                                                }
                                                            </div>
                                                        </div>
                                                        <div className={s.formFooter}>
                                                            <Button onClick={() => remove(index)} danger>Remove</Button>
                                                        </div>
                                                    </div>
                                                    {
                                                        name === fields.length - 1 && renderAdd(add)
                                                    }
                                                </div>
                                            );
                                        })
                                    }
                                    {
                                        fields.length === 0 && renderAdd(add)
                                    }
                                </>
                            );
                        }
                    }
                </Form.List>
            </Form>

        );
    };

    const renderReview = () => {
        if (!initValue?.length) {
            return (
                <div>
                    Not provided
                </div>
            );
        }
        const formatDate = (day: string) => dayjs(day).format('YYYY-MM');
        return initValue?.map((item, index) => {
            const {
                currently,
                endDate,
                organizationName,
                startDate,
                title,
            } = item;

            return (
                <div key={index} className={s.row}>
                    <div className={s.experience}>
                        <div className={s.logoBox}>
                            <div className={s.logo} style={{ backgroundImage: `url(${ExperiencePlaceholder})` }} />
                        </div>
                        <div className={s.intro}>
                            <div className={s.name}>{organizationName}</div>
                            <div className={s.title}>{title}</div>
                            {startDate && (
                                <div className={s.timeloc}>
                                    <span>{formatDate(startDate)} - {currently ? 'Current' : formatDate(endDate)}</span>
                                </div>)}
                        </div>
                    </div>
                </div>
            );
        });
    };

    return (
        <div>
            {isEditing ? renderEditing() : renderReview()}
        </div>
    );
});

export default HealthcareExperience;
